<template>
  <a-modal :open="open" :title="user?.displayName ?? '用户信息'" :footer="null" @cancel="emit('update:open', false)">
    <a-spin :spinning="loading">
      <a-descriptions v-if="detail" :column="1" size="small" bordered>
        <a-descriptions-item label="邮箱">{{ detail.email }}</a-descriptions-item>
        <a-descriptions-item label="状态"><a-tag>{{ statusLabel(detail.status) }}</a-tag></a-descriptions-item>
        <a-descriptions-item label="套餐">{{ planLabel(detail.plan) }}</a-descriptions-item>
        <a-descriptions-item label="注册时间">{{ formatTime(detail.createdAt) }}</a-descriptions-item>
        <a-descriptions-item label="最近登录">{{ formatTime(detail.lastLoginAt) }}</a-descriptions-item>
        <a-descriptions-item label="拥有工作台">{{ detail.workspaceCount }}</a-descriptions-item>
        <a-descriptions-item label="订单数">{{ detail.orderCount }}</a-descriptions-item>
      </a-descriptions>
      <a-empty v-else-if="!loading" :image="false" description="用户信息加载失败" />
    </a-spin>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { adminApi } from '@/api';

type UserSummary = { id: string; displayName: string; email?: string };
type UserDetail = UserSummary & {
  status: 'active' | 'disabled' | 'pendingDeletion';
  plan: 'free' | 'pro' | 'team';
  lastLoginAt: string | null;
  createdAt: string;
  workspaceCount: number;
  orderCount: number;
};

const props = defineProps<{ open: boolean; user?: UserSummary }>();
const emit = defineEmits<{ 'update:open': [value: boolean] }>();
const loading = ref(false);
const detail = ref<UserDetail>();

watch(
  () => [props.open, props.user?.id] as const,
  ([open, userId]) => {
    if (!open || !userId) return;
    void load(userId);
  },
);

async function load(userId: string): Promise<void> {
  loading.value = true;
  detail.value = undefined;
  try {
    detail.value = await adminApi.get<UserDetail>(`/admin/users/${userId}`);
  } catch (error: any) {
    message.error(error?.message ?? '用户信息加载失败');
  } finally {
    loading.value = false;
  }
}

function statusLabel(status: UserDetail['status']): string {
  return ({ active: '正常', disabled: '已停用', pendingDeletion: '待删除' })[status];
}

function planLabel(plan: UserDetail['plan']): string {
  return ({ free: 'Free', pro: 'Pro', team: 'Team' })[plan];
}

function formatTime(value: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '—';
}
</script>
